/* eslint-disable react/jsx-no-bind */
/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  Button,
} from "@mui/material";
import CallEndIcon from "@mui/icons-material/CallEnd";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { useApolloClient } from "@apollo/client";
import {
  ACCEPT_BROWSER_CALL,
  END_BROWSER_CALL,
  SEND_BROWSER_CALL_ICE_CANDIDATE,
  UPLOAD_BROWSER_CALL_RECORDING,
} from "src/graphql/mutation/browserCall/browserCallMutations";
import { GET_BROWSER_CALL_ICE_SERVERS } from "src/graphql/query/browserCall/browserCallQueries";
import { BROWSER_CALL_AGENT_SIGNALING } from "src/graphql/subscriptions/browserCall/browserCallSubscriptions";
import {
  BROWSER_CALL_SESSION_KEY,
  BROWSER_CALL_CHANNEL_NAME,
} from "src/context/browserCall/BrowserCallAgentContext";

type CallState =
  | "loading"
  | "preparing"
  | "connecting"
  | "active"
  | "ended"
  | "error";

type UploadState = "idle" | "uploading" | "uploaded" | "failed";

interface SessionData {
  roomId: string;
  sdpOffer: string;
  customerName: string | null;
  customerIdentifier: string | null;
  channel: string;
  mediaMode: string;
  agentId: string | number | null;
}

const formatDuration = (s: number) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

const AgentCallPopout: React.FC = () => {
  const client = useApolloClient();

  const [callState, setCallState] = useState<CallState>("loading");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isMuted, setIsMuted] = useState(false);
  const [isDeaf, setIsDeaf] = useState(false);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  // Holds the recording blob until upload succeeds. While non-null the
  // window will not auto-close — the user can hit Retry.
  const pendingUploadBlobRef = useRef<Blob | null>(null);

  const sessionRef = useRef<SessionData | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const cleanupRanRef = useRef(false);
  // True iff the peer connection ever reached "connected" — i.e. real media
  // flowed. Lets the backend distinguish a genuine call from one that was
  // accepted but never got past ICE.
  const mediaConnectedRef = useRef(false);

  // Recording (agent-side merged audio)
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Subscription unsub
  const subUnsubRef = useRef<(() => void) | null>(null);

  const setupBroadcast = useCallback(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window))
      return;
    const ch = new BroadcastChannel(BROWSER_CALL_CHANNEL_NAME);
    channelRef.current = ch;
    ch.onmessage = (ev) => {
      if (
        ev.data?.type === "END_FROM_MAIN" &&
        sessionRef.current?.roomId === ev.data.roomId
      ) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        endCall("agent_hangup_from_main");
      }
    };
    ch.postMessage({
      type: "POPOUT_READY",
      roomId: sessionRef.current?.roomId,
    });
  }, []);

  const stopRecorder = useCallback(async () => {
    return new Promise<Blob | null>((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state === "inactive") {
        resolve(null);
        return;
      }
      rec.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        recordedChunksRef.current = [];
        resolve(blob);
      };
      try {
        rec.stop();
      } catch {
        resolve(null);
      }
    });
  }, []);

  const uploadRecording = useCallback(
    async (blob: Blob | null) => {
      if (!blob || !sessionRef.current) return;
      if (blob.size < 1024) return; // ignore essentially empty recordings
      pendingUploadBlobRef.current = blob;
      setUploadState("uploading");
      const file = new File(
        [blob],
        `browser-call-${sessionRef.current.roomId}.webm`,
        {
          type: blob.type || "audio/webm",
        },
      );
      try {
        await client.mutate({
          mutation: UPLOAD_BROWSER_CALL_RECORDING,
          variables: { roomId: sessionRef.current.roomId, file },
          context: { hasUpload: true },
        });
        pendingUploadBlobRef.current = null;
        setUploadState("uploaded");
        // Tell the main tab it can drop its mirrored chunk buffer + cancel
        // the fallback-upload watchdog for this room.
        try {
          channelRef.current?.postMessage({
            type: "RECORDING_UPLOADED",
            roomId: sessionRef.current.roomId,
          });
        } catch {
          /* ignore */
        }
      } catch (err) {
        setUploadState("failed");
        // eslint-disable-next-line no-console
        console.warn("Failed to upload browser-call recording", err);
      }
    },
    [client],
  );

  const retryUpload = useCallback(async () => {
    const blob = pendingUploadBlobRef.current;
    if (!blob) return;
    await uploadRecording(blob);
  }, [uploadRecording]);

  const cleanup = useCallback(async () => {
    if (cleanupRanRef.current) return;
    cleanupRanRef.current = true;
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const blob = await stopRecorder();
    if (audioContextRef.current) {
      try {
        await audioContextRef.current.close();
      } catch {
        /* ignore */
      }
      audioContextRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
      remoteStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      try {
        pcRef.current.close();
      } catch {
        /* ignore */
      }
      pcRef.current = null;
    }
    if (subUnsubRef.current) {
      subUnsubRef.current();
      subUnsubRef.current = null;
    }
    if (channelRef.current) {
      channelRef.current.postMessage({
        type: "POPOUT_ENDED",
        roomId: sessionRef.current?.roomId,
      });
      channelRef.current.close();
      channelRef.current = null;
    }
    await uploadRecording(blob);
  }, [stopRecorder, uploadRecording]);

  const endCall = useCallback(
    async (reason: string) => {
      const room = sessionRef.current?.roomId;
      if (!room) {
        setCallState("ended");
        return;
      }
      setCallState("ended");
      // Tell the main tab right away that *this* pop-out is handling the
      // end of the call. When the subscription's "ended" event arrives
      // there a moment later, the main tab will see this flag and use the
      // 60s watchdog (to let our upload + retry happen) instead of the
      // immediate-fallback path used for dead pop-outs.
      try {
        channelRef.current?.postMessage({
          type: "POPOUT_ENDED",
          roomId: room,
        });
      } catch {
        /* ignore */
      }
      try {
        await client.mutate({
          mutation: END_BROWSER_CALL,
          variables: {
            roomId: room,
            reason,
            mediaConnected: mediaConnectedRef.current,
          },
        });
      } catch {
        /* ignore */
      }
      await cleanup();
      // Close the pop-out a moment after teardown so the user briefly sees
      // "Call ended". Recording upload already finished inside cleanup().
      // window.close() is only allowed because this window was opened with
      // window.open() from the main tab.
      setTimeout(() => {
        // Don't slam the window shut if a recording is still pending /
        // failed — the user needs to retry. They'll close it manually.
        if (pendingUploadBlobRef.current) return;
        try {
          window.close();
        } catch {
          /* ignore */
        }
      }, 1200);
    },
    [client, cleanup],
  );

  const startRecording = useCallback(
    (local: MediaStream, remote: MediaStream) => {
      try {
        const Ctor =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return;
        const ac: AudioContext = new Ctor();
        audioContextRef.current = ac;
        const dest = ac.createMediaStreamDestination();
        ac.createMediaStreamSource(local).connect(dest);
        ac.createMediaStreamSource(remote).connect(dest);
        const merged = dest.stream;
        if (!("MediaRecorder" in window)) return;
        const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
        const rec = new MediaRecorder(merged, { mimeType: mime });
        recordedChunksRef.current = [];
        rec.ondataavailable = (e) => {
          if (!e.data || e.data.size === 0) return;
          recordedChunksRef.current.push(e.data);
          // Mirror each chunk to the main tab so it can do a fallback upload
          // if this pop-out dies before its own upload completes.
          try {
            channelRef.current?.postMessage({
              type: "RECORDING_CHUNK",
              roomId: sessionRef.current?.roomId,
              blob: e.data,
              mimeType: rec.mimeType,
            });
          } catch {
            /* BroadcastChannel may reject huge blobs; safe to ignore */
          }
        };
        rec.start(1000);
        recorderRef.current = rec;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Recording setup failed", err);
      }
    },
    [],
  );

  // Boot: read session, set up peer connection, accept call.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = sessionStorage.getItem(BROWSER_CALL_SESSION_KEY);
        if (!raw) {
          // If the URL still has the roomId, this is a reloaded call window
          // (sessionStorage was cleared after the original Accept). The call
          // is dead — show a clear "Call ended" message and auto-close so
          // the agent isn't staring at a misleading "Open from Accept"
          // screen for a window that did come from Accept.
          let urlRoomId: string | null = null;
          try {
            urlRoomId = new URLSearchParams(window.location.search).get(
              "roomId",
            );
          } catch {
            /* ignore */
          }
          if (urlRoomId) {
            setErrorMsg("Call ended — closing this window...");
            setCallState("error");
            setTimeout(() => {
              try {
                window.close();
              } catch {
                /* ignore */
              }
            }, 1500);
          } else {
            setErrorMsg(
              "Open this window from the Accept button in the inbox.",
            );
            setCallState("error");
          }
          return;
        }
        const session: SessionData = JSON.parse(raw);
        sessionRef.current = session;
        setupBroadcast();

        setCallState("preparing");

        // Fetch ICE servers.
        const iceRes = await client.query({
          query: GET_BROWSER_CALL_ICE_SERVERS,
          fetchPolicy: "network-only",
        });
        const iceServers: RTCIceServer[] = (
          iceRes.data?.getBrowserCallIceServers || []
        ).map((s: any) => ({
          urls: s.urls,
          username: s.username || undefined,
          credential: s.credential || undefined,
        }));

        if (cancelled) return;
        // Grab mic.
        const local = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        localStreamRef.current = local;

        const pc = new RTCPeerConnection({ iceServers });
        pcRef.current = pc;

        local.getTracks().forEach((track) => pc.addTrack(track, local));

        const remote = new MediaStream();
        remoteStreamRef.current = remote;

        pc.ontrack = (ev) => {
          ev.streams[0]?.getTracks().forEach((t) => remote.addTrack(t));
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = remote;
            remoteAudioRef.current.play().catch(() => {});
          }
        };

        pc.onicecandidate = (ev) => {
          if (ev.candidate && sessionRef.current) {
            client
              .mutate({
                mutation: SEND_BROWSER_CALL_ICE_CANDIDATE,
                variables: {
                  roomId: sessionRef.current.roomId,
                  candidate: JSON.stringify(ev.candidate.toJSON()),
                  fromRole: "agent",
                },
              })
              .catch(() => {});
          }
        };

        pc.onconnectionstatechange = () => {
          const st = pc.connectionState;
          if (st === "connected") {
            mediaConnectedRef.current = true;
            setCallState("active");
            if (startedAtRef.current == null) {
              startedAtRef.current = Date.now();
              tickRef.current = setInterval(() => {
                if (startedAtRef.current) {
                  setDurationSeconds(
                    Math.round((Date.now() - startedAtRef.current) / 1000),
                  );
                }
              }, 500);
              startRecording(local, remote);
            }
          } else if (st === "failed" || st === "disconnected") {
            // Treat as ended; the server will reconcile.
            endCall("connection_failed");
          }
        };

        // Subscribe to subsequent ICE candidates + end events from server.
        const obs = client.subscribe({
          query: BROWSER_CALL_AGENT_SIGNALING,
          variables: { agentId: session.agentId },
        });
        const sub = obs.subscribe({
          next: ({ data }) => {
            const payload = data?.browserCallAgentSignaling;
            if (!payload || payload.roomId !== session.roomId) return;
            if (payload.event === "ice_candidate" && payload.candidate) {
              try {
                const cand = JSON.parse(payload.candidate);
                pc.addIceCandidate(new RTCIceCandidate(cand)).catch(() => {});
              } catch {
                /* ignore malformed */
              }
            } else if (payload.event === "ended") {
              endCall("guest_hangup");
            }
          },
          error: () => {
            /* ignore */
          },
        });
        subUnsubRef.current = () => sub.unsubscribe();

        // Apply guest offer, generate answer.
        await pc.setRemoteDescription(
          new RTCSessionDescription({ type: "offer", sdp: session.sdpOffer }),
        );
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        setCallState("connecting");

        await client.mutate({
          mutation: ACCEPT_BROWSER_CALL,
          variables: { roomId: session.roomId, sdpAnswer: answer.sdp || "" },
        });

        channelRef.current?.postMessage({
          type: "POPOUT_ACCEPTED",
          roomId: session.roomId,
        });

        // Clear session so a refresh of the pop-out doesn't replay it.
        try {
          sessionStorage.removeItem(BROWSER_CALL_SESSION_KEY);
        } catch {
          /* ignore */
        }
      } catch (err: any) {
        setErrorMsg(err?.message || "Failed to start call.");
        setCallState("error");
        await cleanup();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close-window safety: end the call if the agent shuts the pop-out.
  useEffect(() => {
    const handler = () => {
      if (sessionRef.current?.roomId) {
        // Fire-and-forget; navigator.sendBeacon would be cleaner but we don't
        // have a REST endpoint for it. The Apollo mutation may not complete.
        try {
          client
            .mutate({
              mutation: END_BROWSER_CALL,
              variables: {
                roomId: sessionRef.current.roomId,
                reason: "window_closed",
                mediaConnected: mediaConnectedRef.current,
              },
            })
            .catch(() => {});
        } catch {
          /* ignore */
        }
      }
    };
    // `pagehide` (not `beforeunload`) — fires only when the page is
    // actually being unloaded. `beforeunload` fires BEFORE the browser
    // shows the "Reload site?" prompt, so handlers on it would end the
    // call even when the user clicks Cancel.
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [client]);

  // Warn the agent if they try to close while a call is active or its
  // recording is still uploading. Closing during a call kills the peer
  // connection; closing mid-upload kills the multipart stream.
  useEffect(() => {
    const shouldWarn = uploadState === "uploading" || callState === "active";
    if (!shouldWarn) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue =
        callState === "active"
          ? "A call is in progress. Are you sure you want to close this window?"
          : "Recording is still uploading. Are you sure you want to close?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [uploadState, callState]);

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !isMuted;
    localStreamRef.current.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
    setIsMuted(next);
  }, [isMuted]);

  const toggleDeaf = useCallback(() => {
    if (!remoteAudioRef.current) return;
    const next = !isDeaf;
    remoteAudioRef.current.muted = next;
    setIsDeaf(next);
  }, [isDeaf]);

  const statusText = useMemo(() => {
    switch (callState) {
      case "loading":
        return "Loading...";
      case "preparing":
        return "Requesting microphone...";
      case "connecting":
        return "Connecting...";
      case "active":
        return formatDuration(durationSeconds);
      case "ended":
        if (uploadState === "uploading") return "Uploading recording...";
        if (uploadState === "uploaded") return "Recording saved";
        if (uploadState === "failed") return "Recording upload failed";
        return "Call ended";
      case "error":
        return errorMsg || "Call failed";
      default:
        return "";
    }
  }, [callState, durationSeconds, errorMsg, uploadState]);

  const customerName =
    sessionRef.current?.customerName ||
    sessionRef.current?.customerIdentifier ||
    "Guest";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "linear-gradient(180deg,#0f172a,#1e293b)",
        color: "#fff",
        alignItems: "center",
        justifyContent: "space-between",
        p: 3,
      }}
    >
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Avatar
          sx={{
            width: 96,
            height: 96,
            bgcolor: "#2563eb",
            mx: "auto",
            mb: 2,
            fontSize: 36,
          }}
        >
          {customerName.slice(0, 1).toUpperCase()}
        </Avatar>
        <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
          {customerName}
        </Typography>
        <Typography sx={{ fontSize: 13, opacity: 0.75, mt: 0.5 }}>
          Browser {sessionRef.current?.mediaMode || "audio"} call
        </Typography>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          {callState === "preparing" ||
          callState === "connecting" ||
          callState === "loading" ||
          uploadState === "uploading" ? (
            <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
          ) : null}
          <Typography sx={{ fontSize: 14 }}>{statusText}</Typography>
        </Box>
        {callState === "ended" && uploadState === "uploading" && (
          <Typography sx={{ fontSize: 11, opacity: 0.7, mt: 1 }}>
            Please keep this window open while the recording uploads.
          </Typography>
        )}
        {callState === "ended" && uploadState === "failed" && (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
              The recording didn't reach the server. Try again, or close this
              window — it will be marked as unavailable in the chat.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={retryUpload}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  borderColor: "#fff",
                  background: "rgba(255,255,255,0.06)",
                },
              }}
            >
              Retry upload
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Tooltip title={isMuted ? "Unmute mic" : "Mute mic"}>
          <span>
            <IconButton
              disabled={callState !== "active"}
              onClick={toggleMute}
              sx={{
                bgcolor: isMuted ? "#dc2626" : "rgba(255,255,255,0.1)",
                color: "#fff",
                "&:hover": {
                  bgcolor: isMuted ? "#b91c1c" : "rgba(255,255,255,0.2)",
                },
              }}
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isDeaf ? "Unmute speaker" : "Mute speaker"}>
          <span>
            <IconButton
              disabled={callState !== "active"}
              onClick={toggleDeaf}
              sx={{
                bgcolor: isDeaf ? "#dc2626" : "rgba(255,255,255,0.1)",
                color: "#fff",
                "&:hover": {
                  bgcolor: isDeaf ? "#b91c1c" : "rgba(255,255,255,0.2)",
                },
              }}
            >
              {isDeaf ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Hang up">
          <IconButton
            onClick={() => endCall("agent_hangup")}
            sx={{
              bgcolor: "#dc2626",
              color: "#fff",
              width: 56,
              height: 56,
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            <CallEndIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <audio ref={remoteAudioRef} autoPlay hidden />
    </Box>
  );
};

export default AgentCallPopout;
